import React, { useState, useEffect, useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../shared/constants/theme';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Typography,
  Button,
  IconButton,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  FormControlLabel,
  Switch,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Avatar,
  Tooltip,
  Stack,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LockIcon from '@mui/icons-material/Lock';
import CodeIcon from '@mui/icons-material/Code';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import {
  getConfigurationActionHeaderRowSx,
  getConfigurationAddButtonSx,
  getConfigurationEmbeddedRootSx,
  isConfigurationMobileLayout,
} from '../utils/configurationMobileLayout';
import {
  getDrawerRootSx,
  getDrawerSelectMenuProps,
} from '../utils/drawerOverlayProps';
import HotelConfiguration from '../components/configuration/HotelConfiguration';
import CommunicationSettings from '../components/configuration/CommunicationSettings';
import axios from 'axios';

// Get CSRF token from cookies
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const CONFIG_TYPE_OPTIONS = [
  { value: 'string', label: 'String', icon: '📝' },
  { value: 'number', label: 'Number', icon: '🔢' },
  { value: 'boolean', label: 'Boolean', icon: '✓' },
  { value: 'json', label: 'JSON', icon: '{}' },
  { value: 'url', label: 'URL', icon: '🔗' },
  { value: 'email', label: 'Email', icon: '✉️' },
  { value: 'date', label: 'Date', icon: '📅' },
  { value: 'datetime', label: 'DateTime', icon: '🕐' },
];

const CATEGORY_OPTIONS = [
  { value: 'general', label: 'General', color: '#1976d2' },
  { value: 'booking', label: 'Booking', color: '#2e7d32' },
  { value: 'payment', label: 'Payment', color: '#ed6c02' },
  { value: 'notification', label: 'Notification', color: '#9c27b0' },
  { value: 'integration', label: 'Integration', color: '#0288d1' },
  { value: 'display', label: 'Display', color: '#d32f2f' },
  { value: 'security', label: 'Security', color: '#c62828' },
  { value: 'other', label: 'Other', color: '#616161' },
];

// Fixed config keys removed - now using HotelConfiguration component for hotel settings

const ConfigPage = ({ showHeader = true }) => {
  const isMobileLayout = isConfigurationMobileLayout();
  const theme = useTheme();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    key: '',
    value: '',
    config_type: 'string',
    category: 'general',
    description: '',
    is_active: true,
    is_encrypted: false,
    sort_order: 0,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const isDark = currentTheme === 'dark';
  const drawerSelectMenuProps = useMemo(
    () => getDrawerSelectMenuProps(isDark, isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff'),
    [isDark],
  );

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const csrftoken = getCookie('csrftoken');
      const response = await axios.get('/configuration/api/configs/', {
        withCredentials: true,
        headers: {
          'Accept': 'application/json',
          ...(csrftoken && { 'X-CSRFToken': csrftoken }),
        },
      });
      if (response.data && response.data.configs) {
        setConfigs(response.data.configs);
      } else {
        setConfigs([]);
      }
    } catch (error) {const errorMessage = error.response?.data?.error || 
                           error.response?.data?.message || 
                           error.message || 
                           'Failed to load configs. Please try again.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDrawer = (config = null) => {
    if (config) {
      setEditingConfig(config);
      setFormData({
        key: config.key,
        value: config.is_encrypted ? '' : config.value,
        config_type: config.config_type,
        category: config.category,
        description: config.description || '',
        is_active: config.is_active,
        is_encrypted: config.is_encrypted,
        sort_order: config.sort_order,
      });
    } else {
      setEditingConfig(null);
      setFormData({
        key: '',
        value: '',
        config_type: 'string',
        category: 'general',
        description: '',
        is_active: true,
        is_encrypted: false,
        sort_order: 0,
      });
    }
    setErrors({});
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setEditingConfig(null);
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.key.trim()) {
      newErrors.key = 'Key is required';
    } else if (formData.key.length > 255) {
      newErrors.key = 'Key must be 255 characters or less';
    }

    if (formData.config_type === 'json') {
      try {
        JSON.parse(formData.value);
      } catch (e) {
        newErrors.value = 'Invalid JSON format';
      }
    }

    if (formData.config_type === 'number') {
      if (isNaN(Number(formData.value))) {
        newErrors.value = 'Invalid number format';
      }
    }

    if (formData.config_type === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.value)) {
        newErrors.value = 'Invalid email format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const url = editingConfig
        ? `/configuration/api/configs/${editingConfig.id}/`
        : '/configuration/api/configs/';
      
      const method = editingConfig ? 'put' : 'post';
      const csrftoken = getCookie('csrftoken');
      
      const response = await axios[method](url, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          ...(csrftoken && { 'X-CSRFToken': csrftoken }),
        },
      });

      if (response.data.success || response.data.config) {
        setSnackbar({
          open: true,
          message: editingConfig ? 'Config updated successfully' : 'Config created successfully',
          severity: 'success',
        });
        handleCloseDrawer();
        fetchConfigs();
      } else if (response.data.errors) {
        setErrors(response.data.errors);
      }
    } catch (error) {if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setSnackbar({
          open: true,
          message: error.response?.data?.error || 'Failed to save config. Please try again.',
          severity: 'error',
        });
      }
    }
  };

  const handleDeleteClick = (config) => {
    // Don't allow deletion of hotel settings (country, timezone, currency)
    if (config.key === 'country' || config.key === 'timezone' || config.key === 'currency') {
      setSnackbar({
        open: true,
        message: 'Hotel settings cannot be deleted. Please use Hotel Settings section to update them.',
        severity: 'warning',
      });
      return;
    }
    setConfigToDelete(config);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!configToDelete) return;

    setDeletingId(configToDelete.id);
    try {
      const csrftoken = getCookie('csrftoken');
      await axios.delete(`/configuration/api/configs/${configToDelete.id}/`, {
        withCredentials: true,
        headers: {
          ...(csrftoken && { 'X-CSRFToken': csrftoken }),
        },
      });
      setSnackbar({
        open: true,
        message: 'Config deleted successfully',
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      setConfigToDelete(null);
      fetchConfigs();
    } catch (error) {setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to delete config. Please try again.',
        severity: 'error',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setConfigToDelete(null);
  };

  // Filter out hotel settings from configs (they're managed via HotelConfiguration component)
  const otherConfigs = configs.filter(c => 
    c.key !== 'country' && c.key !== 'timezone' && c.key !== 'currency'
  );
  const groupedConfigs = otherConfigs.reduce((acc, config) => {
    const category = config.category_display || config.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(config);
    return acc;
  }, {});

  const getCategoryColor = (category) => {
    const cat = CATEGORY_OPTIONS.find(c => c.value === category);
    return cat ? cat.color : '#616161';
  };

  return (
    <Box sx={{ p: showHeader ? 3 : 0, ...getConfigurationEmbeddedRootSx(isMobileLayout) }}>
      {/* Header */}
      {showHeader && (
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: isDark ? alpha('#4ce1b6', 0.2) : alpha('#4ce1b6', 0.1),
                color: '#4ce1b6',
              }}
            >
              <SettingsIcon />
            </Avatar>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage system configuration settings
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDrawer()}
            size="large"
            sx={{
              backgroundColor: '#4ce1b6',
              '&:hover': {
                backgroundColor: isDark ? '#3ab89a' : '#1565c0',
              },
              boxShadow: 2,
            }}
          >
            Add Config
          </Button>
        </Box>
      )}
      

      {/* Hotel Settings Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 3, 
            fontWeight: 600,
            color: isDark ? '#ddd' : '#212121',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <SettingsIcon sx={{ fontSize: 20 }} />
          Hotel Settings
        </Typography>
        <HotelConfiguration />
      </Box>

      <Box sx={{ mb: 4 }}>
        <CommunicationSettings />
      </Box>

      {/* Other Configs by Category */}
      {Object.keys(groupedConfigs).length > 0 && (
        <Box>
          <Box sx={getConfigurationActionHeaderRowSx(isMobileLayout)}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                color: isDark ? '#ddd' : '#212121',
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CodeIcon sx={{ fontSize: 20 }} />
              Configuration
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDrawer()}
              size="large"
              sx={getConfigurationAddButtonSx('#4ce1b6', isMobileLayout)}
            >
              Add Config
            </Button>
          </Box>
          <Grid container spacing={3}>
            {Object.entries(groupedConfigs).map(([category, categoryConfigs]) => {
              const categoryColor = getCategoryColor(category);
              return (
                <Grid item xs={12} key={category}>
                  <Card elevation={2}>
                    <CardHeader
                      title={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 4,
                              height: 24,
                              bgcolor: categoryColor,
                              borderRadius: 1,
                            }}
                          />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {category}
                          </Typography>
                          <Chip
                            label={categoryConfigs.length}
                            size="small"
                            sx={{
                              bgcolor: alpha(categoryColor, 0.1),
                              color: categoryColor,
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      }
                      sx={{
                        bgcolor: isDark ? alpha(categoryColor, 0.1) : alpha(categoryColor, 0.05),
                        borderBottom: `2px solid ${alpha(categoryColor, 0.3)}`,
                      }}
                    />
                    <CardContent>
                      <Grid container spacing={2}>
                        {categoryConfigs.map((config) => (
                          <Grid item xs={12} sm={6} md={4} key={config.id}>
                            <Card
                              variant="outlined"
                              elevation={0}
                              sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'all 0.2s ease-in-out',
                                borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: 4,
                                  borderColor: categoryColor,
                                },
                              }}
                            >
                              <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                                  <Typography 
                                    variant="subtitle1" 
                                    sx={{ 
                                      fontWeight: 600,
                                      fontSize: '0.95rem',
                                      color: isDark ? '#ddd' : '#212121',
                                    }}
                                  >
                                    {config.key}
                                  </Typography>
                                  <Stack direction="row" spacing={0.5}>
                                    <Tooltip title="Edit">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleOpenDrawer(config)}
                                        sx={{ 
                                          color: '#4ce1b6',
                                          '&:hover': {
                                            bgcolor: isDark ? alpha('#4ce1b6', 0.1) : alpha('#4ce1b6', 0.1),
                                          },
                                        }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDeleteClick(config)}
                                        sx={{ 
                                          color: 'error.main',
                                          '&:hover': {
                                            bgcolor: alpha('#d32f2f', 0.1),
                                          },
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </Box>
                                
                                <Paper
                                  variant="outlined"
                                  sx={{
                                    p: 1.5,
                                    mb: 2,
                                    bgcolor: isDark ? '#1a1a1a' : '#f5f5f5',
                                    borderColor: isDark ? '#333' : '#e0e0e0',
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: 'monospace',
                                      wordBreak: 'break-word',
                                      color: isDark ? '#bbb' : '#424242',
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    {config.is_encrypted ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LockIcon fontSize="small" />
                                        <span>***ENCRYPTED***</span>
                                      </Box>
                                    ) : (
                                      config.value
                                    )}
                                  </Typography>
                                </Paper>

                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                  <Chip
                                    label={config.config_type}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      borderColor: alpha(categoryColor, 0.5),
                                      color: categoryColor,
                                    }}
                                  />
                                  <Chip
                                    icon={config.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                                    label={config.is_active ? 'Active' : 'Inactive'}
                                    size="small"
                                    color={config.is_active ? 'success' : 'default'}
                                    variant="outlined"
                                  />
                                  {config.is_encrypted && (
                                    <Chip
                                      icon={<LockIcon />}
                                      label="Encrypted"
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                    />
                                  )}
                                </Stack>

                                {config.description && (
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary" 
                                    sx={{ 
                                      mt: 1.5, 
                                      display: 'block',
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    {config.description}
                                  </Typography>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={48} />
        </Box>
      )}

      {/* Create/Edit Drawer */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={handleCloseDrawer}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          },
        }}
      >
        {/* Fixed Header */}
        <Box
          sx={{
            p: 1.5,
            borderBottom: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
            flexShrink: 0,
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
                {editingConfig ? 'Edit Config' : 'Add New Config'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {editingConfig ? 'Update configuration settings' : 'Create a new configuration entry'}
              </Typography>
            </Box>
            <IconButton 
              onClick={handleCloseDrawer}
              size="small"
              sx={{
                color: isDark ? '#999' : '#666',
                p: 0.5,
                '&:hover': {
                  bgcolor: isDark ? alpha('#fff', 0.1) : alpha('#000', 0.05),
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 3,
            pr: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
            <Stack spacing={3}>
              <TextField
                label="Key"
                value={formData.key}
                onChange={(e) => handleInputChange('key', e.target.value)}
                error={!!errors.key}
                helperText={errors.key}
                required
                disabled={!!editingConfig && (formData.key === 'country' || formData.key === 'timezone' || formData.key === 'currency')}
                fullWidth
                variant="outlined"
              />

              <FormControl fullWidth>
                <InputLabel>Config Type</InputLabel>
                <Select
                  value={formData.config_type}
                  onChange={(e) => handleInputChange('config_type', e.target.value)}
                  label="Config Type"
                  MenuProps={drawerSelectMenuProps}
                  disabled={formData.key === 'country' || formData.key === 'timezone' || formData.key === 'currency'}
                >
                  {CONFIG_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{option.icon}</span>
                        <span>{option.label}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Value"
                value={formData.value}
                onChange={(e) => handleInputChange('value', e.target.value)}
                error={!!errors.value}
                helperText={errors.value}
                required
                fullWidth
                multiline={formData.config_type === 'json'}
                rows={formData.config_type === 'json' ? 6 : 1}
                type={formData.config_type === 'number' ? 'number' : 'text'}
                variant="outlined"
              />

              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  label="Category"
                  MenuProps={drawerSelectMenuProps}
                  disabled={formData.key === 'country' || formData.key === 'timezone' || formData.key === 'currency'}
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: option.color,
                          }}
                        />
                        <span>{option.label}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                placeholder="Optional description for this configuration"
              />

              <TextField
                label="Sort Order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => handleInputChange('sort_order', parseInt(e.target.value) || 0)}
                fullWidth
                variant="outlined"
                helperText="Lower numbers appear first"
              />

              <Paper variant="outlined" sx={{ p: 2, bgcolor: isDark ? '#2a2a2a' : '#fafafa' }}>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) => handleInputChange('is_active', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Active
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Enable this configuration
                        </Typography>
                      </Box>
                    }
                  />

                  {formData.key !== 'country' && formData.key !== 'timezone' && formData.key !== 'currency' && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.is_encrypted}
                          onChange={(e) => handleInputChange('is_encrypted', e.target.checked)}
                          color="warning"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LockIcon fontSize="small" />
                            Encrypted
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Encrypt sensitive values (e.g., API keys, passwords)
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                </Stack>
              </Paper>
            </Stack>
        </Box>

        {/* Fixed Footer */}
        <Box
          sx={{
            p: 1.5,
            borderTop: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
            flexShrink: 0,
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
          }}
        >
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={handleCloseDrawer}
              fullWidth
              size="large"
              sx={{
                borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                color: isDark ? '#ddd' : '#666',
                '&:hover': {
                  borderColor: '#4ce1b6',
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                  color: '#4ce1b6',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              fullWidth
              size="large"
              sx={{
                backgroundColor: '#4ce1b6',
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#4ce1b6',
                  opacity: 0.9,
                  boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                },
              }}
            >
              {editingConfig ? 'Update' : 'Create'}
            </Button>
          </Stack>
        </Box>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            minWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            Delete Config
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the configuration <strong>"{configToDelete?.key}"</strong>? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={handleDeleteCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deletingId === configToDelete?.id}
            startIcon={deletingId === configToDelete?.id ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deletingId === configToDelete?.id ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          elevation={6}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConfigPage;
